import type { MeetingAnalysis, SourceReference } from '@shared/contract';
import { formatDuration } from '../lib/format';

interface Props {
  analysis: MeetingAnalysis;
  title: string;
  date: string;
  onJump: (ref: SourceReference) => void;
}

/** En kvittorad med punktlinje mellan etikett och värde. Värdet är klickbart när det finns en källa. */
function Line({ label, value, refItem, onJump }: { label: string; value: string; refItem?: SourceReference; onJump: (ref: SourceReference) => void }) {
  return (
    <div className="receipt-line">
      <span className="receipt-label">{label}</span>
      <span className="receipt-dots" aria-hidden />
      {refItem ? (
        <button className="receipt-value receipt-jump" title="Visa i transkriptet" onClick={() => onJump(refItem)}>
          {value}
        </button>
      ) : (
        <span className="receipt-value">{value}</span>
      )}
    </div>
  );
}

/** Mötets kvitto: ritas från analysen, så texten är alltid rätt och raderna leder in i transkriptet. */
export function Receipt({ analysis, title, date, onJump }: Props) {
  const { topics, actions, quote, stats } = analysis;
  const main = topics.filter((t) => t.status !== 'side_track');
  const decided = main.filter((t) => t.status === 'decided');
  const unresolved = main.filter((t) => t.status === 'unresolved');
  const unowned = main.filter((t) => t.status === 'unowned');
  const intense = main.filter((t) => t.intense);
  const side = topics.find((t) => t.status === 'side_track');
  const interpreted = unowned.length > 0 || intense.length > 0;

  return (
    <div className="receipt" role="document" aria-label="Mötets kvitto">
      <div className="receipt-head">
        <div className="receipt-brand">SVAVA MEET</div>
        <div>Mötets kvitto</div>
        <div className="receipt-sub">
          {title} · {date}
        </div>
      </div>
      <hr />
      <Line label="Längd" value={`${Math.max(1, Math.round(stats.duration_ms / 60_000))} min`} onJump={onJump} />
      <Line label="Ämnen" value={String(main.length)} onJump={onJump} />
      <Line label="Beslut" value={String(decided.length)} onJump={onJump} />
      <Line label="Olöst" value={String(unresolved.length + unowned.length)} onJump={onJump} />
      <Line label="Att göra" value={String(actions.length)} onJump={onJump} />
      <hr />
      {stats.longest_topic && <Line label="Mest tid på" value={stats.longest_topic} onJump={onJump} />}
      {stats.longest_pause_ms >= 5000 && (
        <Line label="Längsta tystnad" value={`${Math.round(stats.longest_pause_ms / 1000)} s`} onJump={onJump} />
      )}
      {intense.length > 0 && <Line label="Hetast *" value={intense.map((t) => t.name).join(', ')} onJump={onJump} />}

      {decided.length > 0 && (
        <>
          <hr />
          <div className="receipt-section">BESLUT</div>
          {decided.map((t) => (
            <Line key={t.name} label={t.decision ? `${t.name}: ${t.decision}` : t.name} value={formatDuration(t.start_ms)} refItem={t.references[0]} onJump={onJump} />
          ))}
        </>
      )}
      {actions.length > 0 && (
        <>
          <hr />
          <div className="receipt-section">ATT GÖRA</div>
          {actions.map((a, i) => (
            <Line
              key={i}
              label={a.owner ? `${a.owner}: ${a.label}` : a.label}
              value={a.references[0] ? formatDuration(a.references[0].start_ms) : ''}
              refItem={a.references[0]}
              onJump={onJump}
            />
          ))}
        </>
      )}
      {(unresolved.length > 0 || unowned.length > 0) && (
        <>
          <hr />
          {unresolved.map((t) => (
            <Line key={t.name} label={`Inte klart: ${t.name}`} value={formatDuration(t.start_ms)} refItem={t.references[0]} onJump={onJump} />
          ))}
          {unowned.map((t) => (
            <div key={t.name} className="receipt-strong">
              <Line label={`Ingen ansvarig: ${t.name} *`} value={formatDuration(t.start_ms)} refItem={t.references[0]} onJump={onJump} />
            </div>
          ))}
        </>
      )}
      {side && (
        <Line label={`Sidospår: ${side.name}`} value={formatDuration(side.start_ms)} refItem={side.references[0]} onJump={onJump} />
      )}
      {quote && (
        <>
          <hr />
          <div className="receipt-section">DAGENS CITAT</div>
          <button className="receipt-quote" title="Visa i transkriptet" onClick={() => onJump(quote.reference)}>
            ”{quote.text}” <span className="receipt-time">{formatDuration(quote.reference.start_ms)}</span>
          </button>
        </>
      )}
      <hr />
      <div className="receipt-thanks">TACK FÖR MÖTET</div>
      {interpreted && <div className="receipt-note">* AI:ns tolkning. Stämmer det?</div>}
    </div>
  );
}
